import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Preview as PreviewIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { hybridWhatsAppService, WhatsAppMessageData, WhatsAppMessageType } from '../services/hybridWhatsAppService';
import { whatsappTemplatesService } from '../services/whatsappTemplatesService';
import { useSettings } from '../contexts/SettingsContext';

interface WhatsAppMessageSenderProps {
  customerName: string;
  customerPhone: string;
  deviceType: string;
  deviceBrand?: string;
  deviceSize?: string;
  cost?: string;
  orderId?: string;
  estimatedDate?: string;
  requireCallBeforeMaintenance?: boolean;
  onMessageSent?: (success: boolean, messageType: WhatsAppMessageType) => void;
}

const WhatsAppMessageSender: React.FC<WhatsAppMessageSenderProps> = ({
  customerName,
  customerPhone,
  deviceType,
  deviceBrand,
  deviceSize,
  cost,
  orderId,
  estimatedDate,
  requireCallBeforeMaintenance = false,
  onMessageSent
}) => {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [messageType, setMessageType] = useState<WhatsAppMessageType>('deviceReceived');
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // بيانات الرسالة
  const messageData: WhatsAppMessageData = {
    customerName,
    customerPhone,
    deviceType,
    deviceBrand,
    deviceSize,
    cost,
    orderId,
    estimatedDate
  };

  // فتح النافذة
  const handleOpen = () => {
    setOpen(true);
    handlePreview();
  };

  // إغلاق النافذة
  const handleClose = () => {
    setOpen(false);
    setError('');
    setPreview('');
  };

  // معاينة الرسالة
  const handlePreview = async () => {
    setLoading(true);
    try {
      // استخدام خدمة القوالب للمعاينة
      const template = await whatsappTemplatesService.getTemplate(messageType);
      const previewText = whatsappTemplatesService.previewTemplate(template, {
        '{customerName}': messageData.customerName,
        '{deviceBrand}': messageData.deviceBrand || messageData.deviceType,
        '{deviceSize}': messageData.deviceSize || 'غير محدد',
        '{cost}': messageData.cost || 'غير محدد',
        '{orderId}': messageData.orderNumber?.toString() || messageData.orderId || 'غير محدد'
      });
      setPreview(previewText);
      setError('');
    } catch (err) {
      setError('حدث خطأ في معاينة الرسالة');
    } finally {
      setLoading(false);
    }
  };

  // إرسال الرسالة
  const handleSend = async () => {
    setLoading(true);
    try {
      const result = await hybridWhatsAppService.sendMessage(messageType, messageData);

      if (result) {
        setError('');
        handleClose();
        onMessageSent?.(true, messageType);
      } else {
        setError('حدث خطأ أثناء إرسال الرسالة');
      }
    } catch (err) {
      setError('حدث خطأ أثناء إرسال الرسالة');
    } finally {
      setLoading(false);
    }
  };

  // تغيير نوع الرسالة
  const handleMessageTypeChange = (newType: WhatsAppMessageType) => {
    setMessageType(newType);
    // معاينة الرسالة الجديدة
    setTimeout(handlePreview, 100);
  };

  // التحقق من تفعيل الواتساب
  if (!settings.whatsappEnabled) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        color="success"
        startIcon={<WhatsAppIcon />}
        onClick={handleOpen}
        sx={{ 
          borderColor: '#25D366',
          color: '#25D366',
          '&:hover': {
            borderColor: '#128C7E',
            backgroundColor: 'rgba(37, 211, 102, 0.04)'
          }
        }}
      >
        إرسال رسالة واتساب
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          إرسال رسالة واتساب للعميل
        </DialogTitle>
        
        <DialogContent>
          {/* معلومات العميل */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>معلومات العميل:</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`الاسم: ${customerName}`} size="small" />
              <Chip label={`الهاتف: ${customerPhone}`} size="small" />
              <Chip label={`الجهاز: ${deviceType}${deviceSize ? ` ${deviceSize}` : ''}`} size="small" />
              {cost && <Chip label={`التكلفة: ${cost}`} size="small" />}
              {orderId && <Chip label={`رقم الطلب: ${orderId}`} size="small" />}
            </Box>
          </Box>

          {/* اختيار نوع الرسالة */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>نوع الرسالة</InputLabel>
            <Select
              value={messageType}
              onChange={(e) => handleMessageTypeChange(e.target.value as WhatsAppMessageType)}
              label="نوع الرسالة"
            >
              <MenuItem value="deviceReceived">رسالة استلام الجهاز</MenuItem>
              {requireCallBeforeMaintenance && (
                <MenuItem value="deviceInProgress">رسالة التكلفة أثناء العمل</MenuItem>
              )}
              <MenuItem value="deviceCompleted">رسالة اكتمال الإصلاح</MenuItem>
            </Select>
          </FormControl>

          {/* تلميح للعملاء الذين لا يطلبون الاتصال */}
          {!requireCallBeforeMaintenance && (
            <Alert severity="info" sx={{ mb: 2 }}>
              رسالة التكلفة أثناء العمل متاحة فقط للعملاء الذين يطلبون الاتصال قبل الإصلاح
            </Alert>
          )}

          {/* معاينة الرسالة */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              معاينة الرسالة:
            </Typography>
            <TextField
              multiline
              rows={6}
              value={preview}
              fullWidth
              variant="outlined"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: '#f5f5f5' }
              }}
            />
          </Box>

          {/* رسالة الخطأ */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* معلومات إضافية */}
          <Alert severity="info" sx={{ mb: 2 }}>
            سيتم فتح تطبيق الواتساب مع الرسالة جاهزة للإرسال. يمكنك تعديل الرسالة قبل الإرسال إذا أردت.
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            إلغاء
          </Button>
          <Button
            onClick={handlePreview}
            startIcon={<PreviewIcon />}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'معاينة'}
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={loading || !preview}
            sx={{
              bgcolor: '#25D366',
              '&:hover': { bgcolor: '#128C7E' }
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'إرسال'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WhatsAppMessageSender;
